// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

// eslint-disable-next-line
const Quagga: any = require("@gcg/App/Libraries/quagga.js");

import barcodeScannerStyles from "@gcg/App/Components/BarcodeScanner/BarcodeScanner.style";
import {
    drawBoundaries,
    drawOverlay,
    generateImageThumbnail,
    isBannerScannerAvailable,
    quaggaConfig,
} from "@gcg/App/Components/BarcodeScanner/BarcodeScannerHelpers";
import Barcode from "@gcg/XDL/Icons/Barcode";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import logger from "@insite/client-framework/Logger";
import translate from "@insite/client-framework/Translate";
import Button from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import Icon from "@insite/mobius/Icon/Icon";
import Modal from "@insite/mobius/Modal";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { useState } from "react";

const styles = barcodeScannerStyles;

type BarcodeResult = {
    code: string;
    recurrence: number;
    screenshotUrl: string;
    isReady: boolean;
};

type OwnProps = {};

type Props = OwnProps & HasHistory;

let initializing = false;
let results: BarcodeResult[] = [];
let preventDetection = false;
let timeoutHandler: any;
let clearingTimeout = false;

const initQuaggaModule = () => {
    Quagga.onProcessed((result: any) => {
        if (preventDetection) {
            return;
        }

        const drawingCtx = Quagga.canvas.ctx.overlay;
        const drawingCanvas = Quagga.canvas.dom.overlay;

        const ctxWidth = parseInt(drawingCanvas.getAttribute("width"), 10);
        const ctxHeight = parseInt(drawingCanvas.getAttribute("height"), 10);

        if (!result || !result.codeResult || !result.codeResult.code) {
            if (!clearingTimeout) {
                clearingTimeout = true;
                timeoutHandler = setTimeout(() => {
                    drawingCtx.clearRect(0, 0, ctxWidth, ctxHeight);
                    drawBoundaries(drawingCtx, ctxWidth, ctxHeight);
                    clearingTimeout = false;
                }, 300);
            }
        }

        if (result) {
            if (result.box && result.codeResult && result.codeResult.code) {
                clearingTimeout = false;
                clearTimeout(timeoutHandler);
                drawingCtx.clearRect(0, 0, ctxWidth, ctxHeight);
                drawBoundaries(drawingCtx, ctxWidth, ctxHeight);
                drawOverlay(drawingCtx, ctxWidth, ctxHeight, result.box);
                Quagga.ImageDebug.drawPath(result.line, { x: "x", y: "y" }, drawingCtx, {
                    color: "#DC3545",
                    lineWidth: 2,
                });
            }
        }
    });

    Quagga.onDetected((result: any) => {
        if (preventDetection) {
            return;
        }

        const code = result.codeResult.code;

        let matchingResult = results.find((result: BarcodeResult) => result.code === code);
        if (!matchingResult) {
            matchingResult = {
                code,
                recurrence: 0,
            } as BarcodeResult;
            results.push(matchingResult);
        }

        matchingResult.recurrence++;

        if (matchingResult.recurrence >= 3) {
            if (isNewDetection(matchingResult)) {
                results = [];
                const canvas = Quagga.canvas.dom.image;
                const ctxWidth = parseInt(Quagga.canvas.dom.overlay.getAttribute("width"), 10);
                const ctxHeight = parseInt(Quagga.canvas.dom.overlay.getAttribute("height"), 10);
                matchingResult.screenshotUrl = generateImageThumbnail(canvas, result.box, ctxWidth, ctxHeight);
                onNewDetection(matchingResult);
            }
        }
    });
};

let isNewDetection = (matchingResult: BarcodeResult): boolean => false;
let onNewDetection = (matchingResult: BarcodeResult): void => {};

initQuaggaModule();

const handleQuaggaError = (error: any): void => {
    logger.error(error);
};

const BarcodeScanner: React.FC<Props> = ({ history }) => {
    if (!isBannerScannerAvailable()) {
        return null;
    }

    const [isOpen, setIsOpen] = useState(false);
    const [detectedResult, setDetectedResult] = useState<BarcodeResult>({ isReady: false } as BarcodeResult);
    const [isSnapshotVisible, setIsSnapshotVisible] = useState(false);

    isNewDetection = (matchingResult: BarcodeResult) => !detectedResult || detectedResult.code !== matchingResult.code;
    onNewDetection = matchingResult => {
        setDetectedResult({
            ...matchingResult,
            isReady: true,
        });
        showSnapshot();
    };

    const showSnapshot = () => {
        // Wait until snapshot animation is completed and then start detecting new barcodes
        preventDetection = true;
        setIsSnapshotVisible(true);
        setTimeout(() => {
            setIsSnapshotVisible(false);
            preventDetection = false;
        }, 600);
    };

    const barcodeButtonClickHandler = () => {
        if (!initializing) {
            initializing = true;
            setIsOpen(true);
        }
    };

    const closeModal = () => {
        setIsOpen(false);
    };

    const doSearch = () => {
        if (detectedResult.isReady) {
            history.push(`/Search?query=${detectedResult.code}`);
            closeModal();
        }
    };

    const onAfterModalOpen = () => {
        Quagga.init(quaggaConfig, (error: any) => {
            initializing = false;
            if (error) {
                return handleQuaggaError(error);
            }
            Quagga.start();
        });
    };

    const onAfterModalClose = () => {
        results = [];
        Quagga.stop();
        setDetectedResult({ isReady: false } as BarcodeResult);
    };

    return (
        <>
            <Clickable {...styles.barcodeButton} onClick={barcodeButtonClickHandler}>
                <Icon {...styles.barcodeIcon} src={Barcode} />
                <VisuallyHidden>{translate("Search by Barcode")}</VisuallyHidden>
            </Clickable>

            <Modal
                {...styles.mobileBarcodeModal}
                isOpen={isOpen}
                closeOnEsc={true}
                handleClose={closeModal}
                onAfterOpen={onAfterModalOpen}
                onAfterClose={onAfterModalClose}
                headline={translate("Search by Barcode")}
            >
                <StyledWrapper className="viewoport-container clearfix">
                    <div id="barcode-scanner-live-stream" className="viewport">
                        {detectedResult.isReady && (
                            <img
                                className={
                                    isSnapshotVisible
                                        ? "screenshot-of-bar-code img-snapshot-effect"
                                        : "screenshot-of-bar-code"
                                }
                                src={detectedResult.screenshotUrl}
                                alt={translate("Barcode Snapshot image")}
                            />
                        )}
                    </div>
                    {detectedResult.isReady && (
                        <div className="detection">
                            <img src={detectedResult.screenshotUrl} alt={translate("Detected Barcode Image")} />
                            <span className="barcode-number">{detectedResult.code}</span>
                        </div>
                    )}
                </StyledWrapper>
                <StyledWrapper className="modal-actions">
                    <Button onClick={closeModal} variant="secondary" sizeVariant="small">
                        {translate("Close")}
                    </Button>
                    <Button onClick={doSearch} sizeVariant="small">
                        {translate("Search")}
                    </Button>
                </StyledWrapper>
            </Modal>
        </>
    );
};

export default withHistory(BarcodeScanner);
